import {
  Alert,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  styled,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import TopNav from "../components/TopNav";
import pageStyle from "./Settings.module.css";
import { CloudUpload, Google, Upload } from "@mui/icons-material";
import { ChangeEvent, ReactNode, useEffect, useState } from "react";
import {
  APIAddress,
  getBusinessAddress,
  getBusinessLogo,
  getDefaultTaxId,
  getDefaultTerms,
  setBusinessAddress,
  setDefaultTaxId,
  setDefaultTerms,
  setLogo,
} from "../api/settings";
import { apiRequest } from "../utils";
import { APITax, getAllTaxes } from "../api/taxes";
import { APIDriveStatus, copyAllToDrive, getDriveStatus } from "../api/drive";
import { SmsStatus, getSmsStatus } from "../api/sms";
import { useThemeChoice } from "../ThemeRoot";
import { ACCENTS, AccentKey, ThemeMode } from "../theme";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className={pageStyle.section}>
      <div className={pageStyle.sectionIntro}>
        <Typography variant="h6">{title}</Typography>
        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
      </div>
      <div className={pageStyle.sectionBody}>{children}</div>
    </section>
  );
}

function SettingsPage() {
  useEffect(() => {
    getDefaultTerms().then((v) => setTerms(v));
    getDefaultTaxId().then((v) => setTaxId(v));
    getBusinessAddress().then((v) => setAddress(v));
    getBusinessLogo().then((v) => setLogoPreview(v));
    getAllTaxes().then((v) => setTaxesData(v));
    getDriveStatus()
      .then((v) => setDriveStatus(v))
      .catch(() => setDriveStatus(undefined));
  }, []);

  const [driveStatus, setDriveStatus] = useState<APIDriveStatus>();
  const [smsStatus, setSmsStatus] = useState<SmsStatus>();
  const themeChoice = useThemeChoice();
  useEffect(() => {
    getSmsStatus().then(setSmsStatus);
  }, []);
  const [driveMessage, setDriveMessage] = useState("");

  // Poll while a Drive job is running so the result shows up without a reload.
  useEffect(() => {
    if (!driveStatus?.running) {
      return;
    }
    const timer = setTimeout(() => {
      getDriveStatus().then((v) => setDriveStatus(v));
    }, 3000);
    return () => clearTimeout(timer);
  }, [driveStatus]);

  const [taxesData, setTaxesData] = useState<APITax[]>([]);
  const [logoPreview, setLogoPreview] = useState("");

  // Form fields
  const [terms, setTerms] = useState("");
  const [taxId, setTaxId] = useState<string>("");
  const [businessAddress, setAddress] = useState<APIAddress>({
    name: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
    email: "",
    website: "",
  });

  // Handle changes
  const changeTerms = (change: ChangeEvent<HTMLInputElement>) => {
    setTerms(change.target.value);
    setDefaultTerms(change.target.value);
  };

  const changeTaxId = (newTaxId: string) => {
    setTaxId(newTaxId);
    setDefaultTaxId(newTaxId);
  };

  const setUpEmail = () => {
    const token = localStorage.getItem("token");
    apiRequest
      .get("/setup/gmail/start", {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((v) => {
        window.location.href = v.data.url;
      })
      .catch((e) =>
        setDriveMessage(
          e.response?.status === 403
            ? "Only managers can connect the Google account."
            : "Could not reach the server to connect Google."
        )
      );
  };

  const copyToDrive = () => {
    setDriveMessage("");
    copyAllToDrive()
      .then(() => getDriveStatus())
      .then((v) => setDriveStatus({ ...v, running: true }))
      .catch((e) => {
        setDriveMessage(
          e.response?.status === 403
            ? "Only managers can copy data to Google Drive."
            : "Could not start copying to Google Drive."
        );
      });
  };

  const uploadImage = async (e: any) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.addEventListener(
      "load",
      () => {
        setLogo(reader.result as string);
        setLogoPreview(reader.result as string);
      },
      false
    );
    reader.readAsDataURL(file);
  };

  const updateBusinessAddress = (update: Partial<APIAddress>) => {
    if (businessAddress) {
      const newBA = { ...businessAddress, ...update };
      setAddress(newBA);
      setBusinessAddress(newBA);
    }
  };

  const field = (
    key: keyof APIAddress,
    label: string,
    className?: string,
    type?: string
  ) => (
    <FormControl className={className}>
      <InputLabel htmlFor={`${key}Input`}>{label}</InputLabel>
      <OutlinedInput
        onChange={(e) => updateBusinessAddress({ [key]: e.target.value })}
        value={businessAddress[key] || ""}
        id={`${key}Input`}
        label={label}
        type={type}
      />
    </FormControl>
  );

  return (
    <div className="page">
      <TopNav pageTitle="Settings" />
      <main className={pageStyle.main}>
        <div className={pageStyle.content}>
          <Section
            title="Appearance"
            description="How the CRM looks on this device. Each person can choose their own."
          >
            <div>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Mode
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={themeChoice.mode}
                onChange={(_, v) => v && themeChoice.setMode(v as ThemeMode)}
              >
                <ToggleButton value="light">Light</ToggleButton>
                <ToggleButton value="dark">Dark</ToggleButton>
                <ToggleButton value="system">Match device</ToggleButton>
              </ToggleButtonGroup>
            </div>
            <div>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Color
              </Typography>
              <div className={pageStyle.accents}>
                {(Object.keys(ACCENTS) as AccentKey[]).map((key) => (
                  <button
                    key={key}
                    type="button"
                    className={pageStyle.accentOption}
                    data-selected={themeChoice.accent === key}
                    onClick={() => themeChoice.setAccent(key)}
                    aria-label={ACCENTS[key].label}
                  >
                    <span
                      className={pageStyle.accentSwatch}
                      style={{ background: ACCENTS[key].light }}
                    />
                    {ACCENTS[key].label}
                  </button>
                ))}
              </div>
            </div>
          </Section>

          <Section
            title="Business profile"
            description="Shown at the top of every proposal and invoice."
          >
            <div className={pageStyle.logoRow}>
              <div className={pageStyle.logoPreview}>
                {logoPreview ? (
                  <img src={logoPreview} alt="Business logo" />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No logo
                  </Typography>
                )}
              </div>
              <Button
                component="label"
                role={undefined}
                variant="outlined"
                tabIndex={-1}
                startIcon={<Upload />}
              >
                {logoPreview ? "Change Logo" : "Upload Logo"}
                <VisuallyHiddenInput
                  type="file"
                  accept="image/*"
                  onChange={uploadImage}
                />
              </Button>
            </div>
            <div className={pageStyle.grid}>
              {field("name", "Business Name", pageStyle.full)}
              {field("addressLine1", "Address", pageStyle.full)}
              {field("addressLine2", "Address Line 2", pageStyle.full)}
              {field("city", "City", pageStyle.half)}
              {field("state", "State", pageStyle.quarter)}
              {field("zip", "Zip", pageStyle.quarter)}
              {field("phone", "Phone", pageStyle.half, "tel")}
              {field("email", "Email", pageStyle.half, "email")}
              {field("website", "Website", pageStyle.full, "url")}
            </div>
          </Section>

          <Section
            title="Proposal defaults"
            description="Filled in automatically on new proposals."
          >
            <div className={pageStyle.grid}>
              <FormControl className={pageStyle.full}>
                <InputLabel>Default Tax</InputLabel>
                <Select
                  onChange={(e) => changeTaxId(e.target.value)}
                  value={taxId}
                  label="Default Tax"
                >
                  {taxesData.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.title} ({t.percentRate}%)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl className={pageStyle.full}>
                <InputLabel htmlFor="termsInput">
                  Default Terms and Conditions
                </InputLabel>
                <OutlinedInput
                  onChange={changeTerms}
                  value={terms}
                  multiline
                  minRows={4}
                  id="termsInput"
                  label="Default Terms and Conditions"
                />
              </FormControl>
            </div>
          </Section>

          <Section
            title="Google account & Drive"
            description="Used to send emails, add appointments to the calendar, and back up customers, photos and signed contracts to Google Drive."
          >
            <div>
              <Button
                onClick={setUpEmail}
                variant="contained"
                startIcon={<Google />}
              >
                {driveStatus?.enabled ? "Reconnect Google" : "Connect Google"}
              </Button>
            </div>
            {driveStatus &&
              (!driveStatus.enabled ? (
                <Alert severity="info">
                  Customers, photos and proposal PDFs are not being saved to
                  Google Drive yet. Click "Connect Google" and allow Drive
                  access to turn it on.
                </Alert>
              ) : (
                <>
                  <Alert severity="success">
                    New photos, proposal PDFs and signed contracts are saved to
                    Google Drive, and the "Lifetree CRM Data" sheet is updated
                    after every sync. The sheet is a copy: make changes in the
                    CRM, not in the sheet.
                  </Alert>
                  <div className={pageStyle.buttonRow}>
                    {driveStatus.folderUrl && (
                      <Button
                        href={driveStatus.folderUrl}
                        target="_blank"
                        variant="outlined"
                      >
                        Open Drive Folder
                      </Button>
                    )}
                    {driveStatus.sheetUrl && (
                      <Button
                        href={driveStatus.sheetUrl}
                        target="_blank"
                        variant="outlined"
                      >
                        Open CRM Sheet
                      </Button>
                    )}
                    <Button
                      onClick={copyToDrive}
                      disabled={driveStatus.running}
                      variant="outlined"
                      startIcon={<CloudUpload />}
                    >
                      {driveStatus.running
                        ? "Copying to Drive..."
                        : "Copy Existing Data to Drive"}
                    </Button>
                  </div>
                  {!driveStatus.running && driveStatus.lastRun?.error && (
                    <Alert severity="warning">
                      Last Drive update had problems:{" "}
                      {driveStatus.lastRun.error}. Click "Copy Existing Data to
                      Drive" to retry.
                    </Alert>
                  )}
                </>
              ))}
            {driveMessage && <Alert severity="error">{driveMessage}</Alert>}
          </Section>
          <Section
            title="Text messages"
            description="Texting customers from the CRM through Twilio."
          >
            {!smsStatus ? null : smsStatus.enabled ? (
              <>
                <Alert severity="success">
                  Texting is on. Texts are sent from {smsStatus.fromNumber}.
                </Alert>
                {!smsStatus.repliesEnabled && (
                  <Alert severity="warning">
                    Replies aren't set up yet, so customers' answers won't show
                    in the CRM. Set TWILIO_WEBHOOK_URL on the server (see
                    docs/sms.md).
                  </Alert>
                )}
              </>
            ) : (
              <Alert severity="info">
                Texting isn't set up yet. It needs a Twilio account and number;
                the steps are in the API's docs/sms.md. Until then the Text
                button opens your phone's messaging app.
              </Alert>
            )}
          </Section>
        </div>
      </main>
    </div>
  );
}

export default SettingsPage;
